import React, { useState, useEffect } from 'react';
import {
  FiCpu, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiTrendingUp,
  FiAlertTriangle, FiZap, FiStar, FiInfo, FiChevronDown, FiChevronUp,
} from 'react-icons/fi';
import axios from 'axios';

// ─── Health Score Ring ────────────────────────────────────────────────────────
const HealthRing = ({ score }) => {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 10) * circumference;

  const color =
    score >= 8 ? '#22c55e' :
    score >= 6 ? '#3b82f6' :
    score >= 4 ? '#f59e0b' :
    '#ef4444';

  const bg =
    score >= 8 ? 'bg-green-50 border-green-200' :
    score >= 6 ? 'bg-blue-50 border-blue-200' :
    score >= 4 ? 'bg-amber-50 border-amber-200' :
    'bg-red-50 border-red-200';

  const textColor =
    score >= 8 ? 'text-green-700' :
    score >= 6 ? 'text-blue-700' :
    score >= 4 ? 'text-amber-700' :
    'text-red-700';

  return (
    <div className={`flex flex-col items-center justify-center p-6 rounded-2xl border-2 ${bg}`}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle
          cx="55" cy="55" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="55" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill={color}>{score}</text>
        <text x="55" y="68" textAnchor="middle" fontSize="11" fill="#6b7280">/10</text>
      </svg>
      <p className={`text-sm font-bold mt-1 ${textColor}`}>{score >= 8 ? 'Excellent' : score >= 6 ? 'Good' : score >= 4 ? 'Average' : score >= 2 ? 'Needs Work' : 'Critical'}</p>
      <p className="text-xs text-neutral-500 mt-0.5">Campaign Health</p>
    </div>
  );
};

// ─── Score Badge ──────────────────────────────────────────────────────────────
const ScoreBadge = ({ score, max = 10 }) => {
  const pct = score / max;
  const color = pct >= 0.8 ? 'text-green-600 bg-green-50' : pct >= 0.6 ? 'text-blue-600 bg-blue-50' : pct >= 0.4 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${color}`}>{score}/{max}</span>;
};

// ─── Spam Risk Bar ────────────────────────────────────────────────────────────
const SpamBar = ({ score }) => {
  const width = `${score * 10}%`;
  const color = score <= 2 ? 'bg-green-500' : score <= 4 ? 'bg-blue-500' : score <= 6 ? 'bg-amber-500' : 'bg-red-500';
  const label = score <= 2 ? 'Safe' : score <= 4 ? 'Low Risk' : score <= 6 ? 'Medium Risk' : score <= 8 ? 'High Risk' : 'Very High Risk';
  const textColor = score <= 2 ? 'text-green-700' : score <= 4 ? 'text-blue-700' : score <= 6 ? 'text-amber-700' : 'text-red-700';
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-neutral-500">Spam Risk</span>
        <span className={`text-xs font-bold ${textColor}`}>{label} ({score}/10)</span>
      </div>
      <div className="w-full h-2.5 bg-neutral-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width }} />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AICampaignAnalysis = ({ stats }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('qwen2.5:7b');
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    axios.get('/api/ai/models')
      .then(r => {
        setModels(r.data.models || []);
      })
      .catch(() => {}); // silently ignore — models will just be empty
  }, []);

  const handleAnalyze = async () => {
    setLoading(true);
    setError('');
    setAnalysis(null);
    try {
      const response = await axios.post('/api/ai/analyze-campaign', {
        stats,
        model: selectedModel,
      });
      setAnalysis(response.data.analysis);
      setExpanded(true);
    } catch (err) {
      setError(err.response?.data?.error || 'AI analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'subjects', label: 'Subject Tips' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-soft overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <FiCpu className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">AI Campaign Analyst</h3>
            <p className="text-white/70 text-xs">Powered by {selectedModel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Model selector */}
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="text-xs bg-white/15 border border-white/30 text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer"
            style={{ colorScheme: 'dark' }}
          >
            {models.length > 0
              ? models.map((m) => (
                  <option key={m.name} value={m.name} className="text-neutral-900 bg-white">
                    {m.name} ({m.size})
                  </option>
                ))
              : <option value={selectedModel} className="text-neutral-900 bg-white">{selectedModel}</option>
            }
          </select>
          <button
            onClick={handleAnalyze}
            disabled={loading || !stats}
            className="flex items-center gap-2 px-4 py-1.5 bg-white text-primary-700 rounded-lg text-sm font-semibold hover:bg-primary-50 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-medium"
          >
            {loading ? (
              <><FiRefreshCw className="w-4 h-4 animate-spin" /> Analysing…</>
            ) : (
              <><FiZap className="w-4 h-4" /> {analysis ? 'Re-analyze' : 'Analyze Campaign'}</>
            )}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-white transition-all"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-6">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary-100 border-t-primary-600 animate-spin" />
                <FiCpu className="absolute inset-0 m-auto w-6 h-6 text-primary-600" />
              </div>
              <div className="text-center">
                <p className="text-neutral-700 font-semibold">AI is analysing your campaign…</p>
                <p className="text-neutral-400 text-sm mt-1">This may take 15-30 seconds for large models</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Analysis Failed</p>
                <p className="text-xs mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && !analysis && (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
              <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center">
                <FiCpu className="w-8 h-8 text-primary-400" />
              </div>
              <div>
                <p className="text-neutral-600 font-semibold">Ready to analyse your campaign</p>
                <p className="text-neutral-400 text-sm mt-1">
                  Click <strong>"Analyze Campaign"</strong> to get AI-powered insights, recommendations, and health scores for your email campaign.
                </p>
              </div>
            </div>
          )}

          {/* Results */}
          {!loading && analysis && (
            <div className="space-y-6">
              {/* Top row: health ring + summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <HealthRing score={analysis.healthScore || 0} />
                <div className="md:col-span-2 space-y-3">
                  <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                    <p className="text-sm text-neutral-700 leading-relaxed">{analysis.summary}</p>
                  </div>
                  {/* Positives */}
                  {analysis.positives?.length > 0 && (
                    <div className="space-y-1.5">
                      {analysis.positives.map((p, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-green-700">
                          <FiCheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-500" />
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Concerns */}
                  {analysis.concerns?.length > 0 && (
                    <div className="space-y-1.5">
                      {analysis.concerns.map((c, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                          <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                          <span>{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tab nav */}
              <div className="border-b border-neutral-200">
                <div className="flex gap-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'border-primary-600 text-primary-700 bg-primary-50'
                          : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab: Overview (benchmarks) */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Overall Score', value: <ScoreBadge score={analysis.healthScore} />, icon: FiStar, bg: 'bg-primary-50 border-primary-100' },
                    { label: 'Recommendations', value: analysis.recommendations?.length || 0, icon: FiTrendingUp, bg: 'bg-blue-50 border-blue-100' },
                    { label: 'Concerns', value: analysis.concerns?.length || 0, icon: FiAlertTriangle, bg: 'bg-amber-50 border-amber-100' },
                    { label: 'Subject Tips', value: analysis.subjectLineTips?.length || 0, icon: FiInfo, bg: 'bg-green-50 border-green-100' },
                  ].map(({ label, value, icon: Icon, bg }) => (
                    <div key={label} className={`rounded-xl p-4 border ${bg} text-center`}>
                      <Icon className="w-5 h-5 mx-auto mb-2 text-neutral-500" />
                      <p className="text-xl font-bold text-neutral-800">{value}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Tab: Recommendations */}
              {activeTab === 'recommendations' && (
                <div className="space-y-3">
                  {analysis.recommendations?.map((rec, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-neutral-50 rounded-xl border border-neutral-100 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200">
                      <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-800">{rec.title}</p>
                        <p className="text-sm text-neutral-600 mt-0.5">{rec.detail}</p>
                      </div>
                    </div>
                  ))}
                  {(!analysis.recommendations || analysis.recommendations.length === 0) && (
                    <p className="text-center text-neutral-400 py-6">No recommendations generated.</p>
                  )}
                </div>
              )}

              {/* Tab: Subject Line Tips */}
              {activeTab === 'subjects' && (
                <div className="space-y-2">
                  {analysis.subjectLineTips?.map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                      <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-neutral-700">{tip}</p>
                    </div>
                  ))}
                  {(!analysis.subjectLineTips || analysis.subjectLineTips.length === 0) && (
                    <p className="text-center text-neutral-400 py-6">No subject line tips generated.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AICampaignAnalysis;
